// Web Audio + haptics. Synth-only (no asset loads). Lazy init on first play
// so the AudioContext isn't created until the user has interacted.

export type SoundEvent =
  | 'paddle'
  | 'perfect'
  | 'brick'
  | 'break'
  | 'pickup'
  | 'bomb'
  | 'save'
  | 'overdrive'
  | 'achievement'
  | 'high_score'
  | 'pulse'
  | 'boss_down';

export interface AudioSettings {
  sound: boolean;
  haptics: boolean;
  reducedMotion: boolean;
}

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private settings: AudioSettings;
  private lastPlayedAt = new Map<SoundEvent, number>();

  constructor(settings: AudioSettings) {
    this.settings = settings;
  }

  setSettings(s: AudioSettings): void {
    this.settings = s;
  }

  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (!this.settings.sound) return null;
    const Ctor = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.32;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  play(event: SoundEvent, pitch = 1): void {
    if (!this.settings.sound) return;
    const ctx = this.ensureCtx();
    const master = this.master;
    if (!ctx || !master) return;
    // Drop rapid duplicates inside a 10ms window.
    const now = ctx.currentTime;
    const last = this.lastPlayedAt.get(event) ?? 0;
    if (now - last < 0.01) return;
    this.lastPlayedAt.set(event, now);

    switch (event) {
      case 'paddle': this.tick(ctx, master, now, 600 * pitch, 0.05, 'sine', 0.18); break;
      case 'perfect': this.tick(ctx, master, now, 880 * pitch, 0.09, 'triangle', 0.28); break;
      case 'brick': this.tick(ctx, master, now, 420 * pitch, 0.07, 'square', 0.18); break;
      case 'break': this.crackle(ctx, master, now, 0.18, 0.25); break;
      case 'pickup': this.chord(ctx, master, now, [523, 659, 784], 0.22, 0.22); break;
      case 'bomb': this.thump(ctx, master, now, 60, 0.35, 0.45); break;
      case 'save': this.thump(ctx, master, now, 90, 0.55, 0.5); break;
      case 'overdrive': this.sweep(ctx, master, now, 220, 880, 0.5, 0.4); break;
      case 'achievement': this.ding(ctx, master, now, [659, 880, 1175], 0.4); break;
      case 'high_score': this.ding(ctx, master, now, [523, 784, 1046, 1568], 0.6); break;
      case 'pulse': this.sweep(ctx, master, now, 1200, 200, 0.18, 0.3); break;
      case 'boss_down': this.thump(ctx, master, now, 55, 0.6, 0.55); this.sweep(ctx, master, now + 0.05, 800, 80, 0.4, 0.4); break;
    }
  }

  private tick(
    ctx: AudioContext, master: GainNode, t: number,
    freq: number, dur: number, shape: OscillatorType, vol: number,
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = shape;
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(60, freq * 0.6), t + dur);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0005, t + dur);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private crackle(ctx: AudioContext, master: GainNode, t: number, dur: number, vol: number): void {
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const env = 1 - i / data.length;
      data[i] = (Math.random() * 2 - 1) * env * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.value = 1600;
    const gain = ctx.createGain();
    gain.gain.value = vol;
    src.connect(filt).connect(gain).connect(master);
    src.start(t);
  }

  private chord(
    ctx: AudioContext, master: GainNode, t: number,
    freqs: number[], dur: number, vol: number,
  ): void {
    for (const f of freqs) this.tick(ctx, master, t, f, dur, 'triangle', vol / freqs.length);
  }

  private thump(ctx: AudioContext, master: GainNode, t: number, freq: number, dur: number, vol: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * 2, t);
    osc.frequency.exponentialRampToValueAtTime(freq, t + 0.08);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private sweep(
    ctx: AudioContext, master: GainNode, t: number,
    f0: number, f1: number, dur: number, vol: number,
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(f0, t);
    osc.frequency.exponentialRampToValueAtTime(f1, t + dur);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol, t + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private ding(
    ctx: AudioContext, master: GainNode, t: number,
    notes: number[], dur: number,
  ): void {
    notes.forEach((f, i) => {
      this.tick(ctx, master, t + i * 0.07, f, dur, 'triangle', 0.28);
    });
  }

  vibe(pattern: number | number[]): void {
    if (!this.settings.haptics) return;
    const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
    if (typeof nav.vibrate !== 'function') return;
    nav.vibrate(pattern);
  }
}

export function defaultAudioSettings(): AudioSettings {
  return {
    sound: true,
    haptics: true,
    reducedMotion: false,
  };
}
